import { useMediaQuery } from '@podiumhq/design-system';

import useTrialProtectedPageGuard from 'components/Trials/hooks/useTrialProtectedPageGuard';
import BusinessInfoAnimations from 'components/Trials/pages/BusinessInfo/components/BusinessInfoAnimations';
import { useBusinessInfoContext } from 'contexts/BusinessContextProvider';
import { useNotificationContext } from 'contexts/NotificationContextProvider';
import { useTrialsContext } from 'contexts/TrialsContextProvider';
import useChangePage from 'hooks/useChangePage';
import useTrackAmplitudeEvent from 'hooks/useTrackAmplitudeEvent';
import { TRIAL_URL } from 'routes/constants';

import AccountError from 'components/Trials/components/AccountError';
import { PROFILE_ROUTE } from 'components/Trials/constants';
import useSubmitCreateTrial from 'components/Trials/hooks/useSubmitCreateTrial';
import { FakeWrapper } from 'components/Trials/layout';
import ModalWrapper from 'components/Trials/Modals/ModalWrapper';

import CreatingBusinessNumber from '../LoadingState/CreatingBusinessNumber';

import BusinessInfoContent from './components/BusinessInfoContent';

const BusinessInfo = () => {
  const { validateBusinessInfo, businessName, gmb, businessInfo } =
    useBusinessInfoContext() || {};
  const changePage = useChangePage();
  const [isSmallerThan650px] = useMediaQuery('(max-width: 650px)');
  const { shouldDisplay, setShouldDisplay } = useNotificationContext() || {};
  const createNewTrialNoCreditCard = useSubmitCreateTrial();
  const trackEvent = useTrackAmplitudeEvent('trials.businessInfo.visited');
  const {
    accountExists,
    hasValidLogin,
    hasValidName,
    isSubmittingTrial,
    setIsSubmittingTrial,
  } = useTrialsContext() || {};
  useTrialProtectedPageGuard({
    shouldHaveValidLogin: true,
    shouldHaveVerifiedAccount: true,
  });

  const validateTrialData = async () => {
    const hasValidBusinessInfo = validateBusinessInfo({ trackEvent });
    const validAccount = await hasValidLogin();
    const validName = hasValidName();
    const isValid =
      !accountExists && validAccount && hasValidBusinessInfo && validName;

    if (isValid) {
      return isValid;
    } else if (!validAccount) {
      return changePage(TRIAL_URL, true);
    } else if (!validName) {
      return changePage(PROFILE_ROUTE, true);
    }
  };

  const validateAndSubmitTrialData = async () => {
    const hasValidTrialData = await validateTrialData();

    if (hasValidTrialData) {
      await createNewTrialNoCreditCard(true);
    } else {
      setIsSubmittingTrial(false);
    }
  };

  const handleSubmit = () => {
    setIsSubmittingTrial(true);
    setShouldDisplay(false);
    trackEvent('trials.businessInfo.nextClicked');

    validateAndSubmitTrialData().then(() =>
      trackEvent('trials.startTrial.startTrialSubmitted')
    );
  };

  return shouldDisplay ? (
    <FakeWrapper showIcon={false} padding={isSmallerThan650px ? '0' : '24px'}>
      <AccountError
        handleSubmit={handleSubmit}
        isSubmitting={isSubmittingTrial}
      />
    </FakeWrapper>
  ) : isSubmittingTrial ? (
    <CreatingBusinessNumber />
  ) : (
    <ModalWrapper
      onPrimaryCtaClick={handleSubmit}
      primaryCta="Continue"
      progress={66}
    >
      <BusinessInfoContent isSubmitting={isSubmittingTrial} />

      <BusinessInfoAnimations
        businessName={businessName}
        gmbReviews={businessInfo?.gmbReviews}
        showReview={gmb?.placeId}
      />
    </ModalWrapper>
  );
};

export default BusinessInfo;
